import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import AdminSidebar from '../components/AdminSidebar';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  RefreshCw,
  User,
  Folder,
  FolderOpen,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Film
} from 'lucide-react';
import FolderBrowser from '../components/FolderBrowser';
import { UserUploadsDialog } from '../components/UserUploadsDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const AdminUsers = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'programmer',
    email: '',
    phone: '',
    assigned_folder_path: '',
    max_file_size_gb: '',
    storage_quota_gb: '',
    can_delete_files: false
  });
  const [isFolderBrowserOpen, setIsFolderBrowserOpen] = useState(false);
  const [uploadsUser, setUploadsUser] = useState(null);

  const handleViewAsUser = async (user) => {
    try {
      const res = await axios.post(`${API}/admin/impersonate/${user.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Store admin token for return
      sessionStorage.setItem('admin_token', token);
      sessionStorage.setItem('admin_user', JSON.stringify({ role: 'admin' }));
      // Set impersonated user session
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      // Redirect to upload portal
      window.location.href = '/upload';
    } catch (err) {
      toast.error('Failed to impersonate user');
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      role: 'programmer',
      email: '',
      phone: '',
      assigned_folder_path: '',
      max_file_size_gb: '',
      storage_quota_gb: '',
      can_delete_files: false
    });
  };

  const handleCreate = async () => {
    try {
      const payload = {
        username: formData.username,
        password: formData.password,
        role: formData.role,
        email: formData.email || null,
        phone: formData.phone || null,
        assigned_folder_path: formData.assigned_folder_path || null,
        max_file_size_gb: formData.max_file_size_gb ? parseFloat(formData.max_file_size_gb) : null,
        storage_quota_gb: formData.storage_quota_gb ? parseFloat(formData.storage_quota_gb) : null,
        can_delete_files: formData.can_delete_files
      };

      await axios.post(`${API}/admin/users`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('User created successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEdit = async () => {
    try {
      const payload = {};
      if (formData.username) payload.username = formData.username;
      if (formData.password) payload.password = formData.password;
      if (formData.role) payload.role = formData.role;
      payload.email = formData.email || null;
      payload.phone = formData.phone || null;
      if (formData.assigned_folder_path !== undefined) payload.assigned_folder_path = formData.assigned_folder_path || null;
      if (formData.max_file_size_gb !== undefined) payload.max_file_size_gb = formData.max_file_size_gb ? parseFloat(formData.max_file_size_gb) : null;
      if (formData.storage_quota_gb !== undefined) payload.storage_quota_gb = formData.storage_quota_gb ? parseFloat(formData.storage_quota_gb) : null;
      payload.can_delete_files = formData.can_delete_files;

      await axios.put(`${API}/admin/users/${selectedUser.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('User updated successfully');
      setIsEditOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API}/admin/users/${user.id}`, {
        is_active: !user.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`User ${user.is_active ? 'disabled' : 'enabled'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('User deleted');
      setIsDeleteOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '',
      role: user.role,
      email: user.email || '',
      phone: user.phone || '',
      assigned_folder_path: user.assigned_folder_path || '',
      max_file_size_gb: user.max_file_size_gb?.toString() || '',
      storage_quota_gb: user.storage_quota_gb?.toString() || '',
      can_delete_files: user.can_delete_files || false
    });
    setIsEditOpen(true);
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#09090B] flex" data-testid="admin-users">
      <AdminSidebar />
      
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto pt-16 lg:pt-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="font-display text-2xl sm:text-4xl text-white tracking-tight">USER MANAGEMENT</h1>
              <p className="text-zinc-500 mt-1 text-sm sm:text-base">Create and manage programmer accounts</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchUsers}
                className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 flex-1 sm:flex-none"
              >
                <RefreshCw className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button
                onClick={() => { resetForm(); setIsCreateOpen(true); }}
                className="bg-red-600 hover:bg-red-700 text-white btn-glow flex-1 sm:flex-none"
                data-testid="create-user-btn"
              >
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Add User</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-4 sm:mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-900 border-zinc-800 text-white h-10"
              data-testid="search-users-input"
            />
          </div>

          {/* Users Table - Desktop */}
          <div className="hidden md:block bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
            <ScrollArea className="h-[600px]">
              <Table>
                <TableHeader className="sticky top-0 bg-zinc-900 z-10">
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">User</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Role</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Status</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Folder</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Stats</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Last Login</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs tracking-wider text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-zinc-500">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map(user => (
                      <TableRow 
                        key={user.id} 
                        className="border-zinc-800 hover:bg-zinc-800/50"
                        data-testid={`user-row-${user.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-zinc-800 flex items-center justify-center">
                              <User className="w-4 h-4 text-zinc-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{user.username}</p>
                              {user.email && <p className="text-zinc-500 text-xs">{user.email}</p>}
                              {user.phone && <p className="text-zinc-500 text-xs">{user.phone}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 text-xs uppercase ${
                            user.role === 'admin' 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {user.role}
                          </span>
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => handleToggleActive(user)}
                            className="flex items-center gap-2 group"
                            data-testid={`toggle-active-${user.id}`}
                          >
                            {user.is_active ? (
                              <>
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <span className="text-green-500 text-xs group-hover:underline">Active</span>
                              </>
                            ) : (
                              <>
                                <XCircle className="w-4 h-4 text-red-500" />
                                <span className="text-red-500 text-xs group-hover:underline">Disabled</span>
                              </>
                            )}
                          </button>
                        </TableCell>
                        <TableCell>
                          {user.assigned_folder_path ? (
                            <div className="flex items-center gap-1 text-zinc-400 text-xs">
                              <Folder className="w-3 h-3" />
                              <span className="font-mono truncate max-w-[150px]" title={user.assigned_folder_path}>
                                {user.assigned_folder_path.split('/').pop()}
                              </span>
                            </div>
                          ) : (
                            <span className="text-zinc-600 text-xs">Not set</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            <p className="text-zinc-400">{user.total_uploads || 0} uploads</p>
                            <p className="text-zinc-500">{formatBytes(user.total_size_uploaded || 0)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-zinc-500 text-xs">
                            <Clock className="w-3 h-3" />
                            {formatDate(user.last_login_at)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setUploadsUser(user)}
                              className="h-8 w-8 text-zinc-400 hover:text-green-400"
                              title="View uploads"
                              data-testid={`view-uploads-table-btn-${user.id}`}
                            >
                              <Film className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewAsUser(user)}
                              className="h-8 w-8 text-zinc-400 hover:text-blue-400"
                              title="View as this user"
                              data-testid={`view-as-user-table-btn-${user.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(user)}
                              className="h-8 w-8 text-zinc-400 hover:text-white"
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setSelectedUser(user); setIsDeleteOpen(true); }}
                              className="h-8 w-8 text-zinc-400 hover:text-red-400"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>

          {/* Users Cards - Mobile */}
          <div className="md:hidden space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12 text-zinc-500 bg-zinc-900/50 border border-zinc-800 rounded-xl">
                No users found
              </div>
            ) : (
              filteredUsers.map(user => (
                <div
                  key={user.id}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4"
                  data-testid={`user-card-${user.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-zinc-800 rounded flex items-center justify-center">
                        <User className="w-5 h-5 text-zinc-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{user.username}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 text-xs uppercase ${
                            user.role === 'admin' 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {user.role}
                          </span>
                          {user.is_active ? (
                            <span className="flex items-center gap-1 text-green-500 text-xs">
                              <CheckCircle className="w-3 h-3" /> Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-500 text-xs">
                              <XCircle className="w-3 h-3" /> Disabled
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setUploadsUser(user)}
                        className="h-8 w-8 text-zinc-400 hover:text-green-400"
                        title="View uploads"
                        data-testid={`view-uploads-btn-${user.id}`}
                      >
                        <Film className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleViewAsUser(user)}
                        className="h-8 w-8 text-zinc-400 hover:text-blue-400"
                        title="View as this user"
                        data-testid={`view-as-user-btn-${user.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(user)}
                        className="h-8 w-8 text-zinc-400 hover:text-white"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setSelectedUser(user); setIsDeleteOpen(true); }}
                        className="h-8 w-8 text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="text-zinc-500">
                      <span className="block text-zinc-600 uppercase">Folder</span>
                      {user.assigned_folder_path ? (
                        <span className="flex items-center gap-1 truncate">
                          <Folder className="w-3 h-3 flex-shrink-0" />
                          {user.assigned_folder_path.split('/').pop()}
                        </span>
                      ) : (
                        <span className="text-zinc-600">Not set</span>
                      )}
                    </div>
                    <div className="text-zinc-500">
                      <span className="block text-zinc-600 uppercase">Stats</span>
                      {user.total_uploads || 0} uploads • {formatBytes(user.total_size_uploaded || 0)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Create User Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">CREATE USER</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 uppercase text-xs">Username</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                data-testid="create-username-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400 uppercase text-xs">Password</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                data-testid="create-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400 uppercase text-xs">Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="programmer">Programmer</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@email.com"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="create-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs">Phone</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1234567890"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="create-phone-input"
                />
              </div>
            </div>
            <p className="text-xs text-zinc-500">Email/phone needed for rejection notifications</p>
            
            <div className="space-y-2">
              <Label className="text-zinc-400 uppercase text-xs">Assigned Folder Path</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.assigned_folder_path}
                  onChange={(e) => setFormData({ ...formData, assigned_folder_path: e.target.value })}
                  placeholder="/path/to/folder"
                  className="flex-1 bg-zinc-800 border-zinc-700 font-mono text-sm"
                  data-testid="create-folder-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsFolderBrowserOpen(true)}
                  className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-700"
                  data-testid="browse-folder-btn"
                >
                  <FolderOpen className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs">Max File Size (GB)</Label>
                <Input
                  type="number"
                  value={formData.max_file_size_gb}
                  onChange={(e) => setFormData({ ...formData, max_file_size_gb: e.target.value })}
                  placeholder="10"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs">Storage Quota (GB)</Label>
                <Input
                  type="number"
                  value={formData.storage_quota_gb}
                  onChange={(e) => setFormData({ ...formData, storage_quota_gb: e.target.value })}
                  placeholder="100"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between py-2">
              <Label className="text-zinc-400">Can Delete Files</Label>
              <Switch
                checked={formData.can_delete_files}
                onCheckedChange={(v) => setFormData({ ...formData, can_delete_files: v })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsCreateOpen(false)} className="text-zinc-400">
              Cancel
            </Button>
            <Button 
              onClick={handleCreate} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="create-user-submit"
            >
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">EDIT USER</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 uppercase text-xs">Username</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                data-testid="edit-username-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400 uppercase text-xs">New Password (leave blank to keep)</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="••••••••"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400 uppercase text-xs">Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="programmer">Programmer</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@email.com"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="edit-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs">Phone</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1234567890"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="edit-phone-input"
                />
              </div>
            </div>
            <p className="text-xs text-zinc-500">Email/phone needed for rejection notifications</p>
            
            <div className="space-y-2">
              <Label className="text-zinc-400 uppercase text-xs">Assigned Folder Path</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.assigned_folder_path}
                  onChange={(e) => setFormData({ ...formData, assigned_folder_path: e.target.value })}
                  placeholder="/path/to/folder"
                  className="flex-1 bg-zinc-800 border-zinc-700 font-mono text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsFolderBrowserOpen(true)}
                  className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-700"
                >
                  <FolderOpen className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs">Max File Size (GB)</Label>
                <Input
                  type="number"
                  value={formData.max_file_size_gb}
                  onChange={(e) => setFormData({ ...formData, max_file_size_gb: e.target.value })}
                  placeholder="10"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs">Storage Quota (GB)</Label>
                <Input
                  type="number"
                  value={formData.storage_quota_gb}
                  onChange={(e) => setFormData({ ...formData, storage_quota_gb: e.target.value })}
                  placeholder="100"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between py-2">
              <Label className="text-zinc-400">Can Delete Files</Label>
              <Switch
                checked={formData.can_delete_files}
                onCheckedChange={(v) => setFormData({ ...formData, can_delete_files: v })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsEditOpen(false)} className="text-zinc-400">
              Cancel
            </Button>
            <Button 
              onClick={handleEdit} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="edit-user-submit"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Folder Browser */}
      <FolderBrowser
        open={isFolderBrowserOpen}
        onClose={() => setIsFolderBrowserOpen(false)}
        onSelect={(path) => setFormData({ ...formData, assigned_folder_path: path })}
        initialPath={formData.assigned_folder_path || '/app/uploads'}
      />

      <UserUploadsDialog user={uploadsUser} token={token} onClose={() => setUploadsUser(null)} />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">DELETE USER</DialogTitle>
          </DialogHeader>
          
          <p className="text-zinc-400 py-4">
            Are you sure you want to delete <span className="text-white font-medium">{selectedUser?.username}</span>? 
            This action cannot be undone.
          </p>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsDeleteOpen(false)} className="text-zinc-400">
              Cancel
            </Button>
            <Button 
              onClick={handleDelete} 
              variant="destructive"
              data-testid="confirm-delete-user"
            >
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
